function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from 'react';
import produce from 'immer';
import uniq from 'lodash/uniq';
import { WritableDraft } from 'immer/dist/types/types-external';
import { EntrySectionUpdates, EntryType, MetricEntryBehavior, QueryEventEntry, QuerySegmentEntry, RowLabelType, SelectorTypeDefinitions } from 'common/query-builder/query-entry-section/types';
import { DateRange } from 'common/widgets/date-range-picker/models/date-range';
import { BehaviorEventRow } from 'common/query-builder/query-entry-section/row-types/new-metric-entry/common/event-row';
import { getEmptyEventMetricEntryBehavior } from 'common/query-builder/query-entry-section/row-types/new-metric-entry/utils';
import { mapBookmarkExclusionStepToEventEntry, mapEventEntryToBookmarkExclusionStep } from 'common/report/funnels/models/format';
import { useTranslation } from 'common/i18n/hooks';
import { DraggableBehavior, DraggableBehaviorsList, useBehaviorsList } from 'common/query-builder/query-entry-section/row-types/new-metric-entry/common/behaviors-list';
import { MAX_HOLDING_PROPERTY_CONSTANT } from 'common/report/constants';
import { FunnelOrder, FunnelReentryModeType, MetricType } from 'common/types/reports/bookmark';
import { ConversionCriteriaAction } from 'common/query-builder/conversion-query-options/types';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { getFunnelOrRetentionDataGroupId } from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/behavior-utils';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { ButtonSize } from 'common/components/button/constants';
import { Placement } from 'common/components/tooltip/types';
import { Directions } from 'common/components/drop-menu/types';
import { EXCLUSION_EVENTS_TOOLTIP, HOLD_PROPERTY_CONSTANT_DISABLED_TOOLTIP, HOLD_PROPERTY_CONSTANT_TOOLTIP, OPTIMIZED_REENTRY_TOOLTIP, ORDER_OPTIONS } from 'common/report/funnels/models/constants';
import { Segmentation } from 'common/types/reports/query';
import { ChangeEvent } from 'common/components/select/types';
import { QueryEntrySectionReact } from 'common/query-builder/query-entry-section';
import { IncludeCustomPropertyType, PropertyCombineMode } from 'common/query-builder/query-block/properties-menu/types';
import { sanitizeExclusionSteps } from 'common/query-builder/query-builder/util';
import { ControlBarAction, ControlBarActionItems } from 'common/query-builder/query-entry-section/constants';
import { RowProps } from 'common/query-builder/query-entry-section/row-types/new-metric-entry/types/catalog';
import { SwitchEl } from 'common/components/switch';
import { QueryEntrySectionTrackingEvent } from 'common/query-builder/query-entry-section/tracking';
import { isPlaceholder } from 'common/query-builder/query-entry-section/row-types/new-metric-entry/helpers';
import styles from './index.module.styl';
import { ConversionCriteria } from './conversion-criteria';
import { EventComparisonRow } from './event-comparison-row';
import { toggleAnyOrder, useFunnelAnyOrderIconToggle } from './util';

/**
 * Component that defines the behavior of a funnel in the query builder.
 *
 * @remarks
 * This component handles the rendering and updating of funnel behaviors, including:
 * - Adding, deleting, and updating the events in the funnel.
 * - Handling nested event comparisons.
 * - Managing exclusion steps and holding property constants.
 * - Toggling funnel order between "Any" and "Loose" for each event / or event
 *   comparison behavior.
 *    - NOTE: Does not allow toggling an empty/invalid event inside the funnel.
 *
 * @todo(metrics)
 * - Update the style to match the design.
 */
export function FunnelBehaviorDefinition(props: RowProps) {
  var _useCurrentProject, _props$sectionProps, _ORDER_OPTIONS$find, _exclusionSteps$map;
  if (!props.entry.behavior || !props.entry.behavior.behaviors) {
    return null;
  }
  const currentProject = (_useCurrentProject = useCurrentProject()) === null || _useCurrentProject === void 0 || (_useCurrentProject = _useCurrentProject.get()) === null || _useCurrentProject === void 0 ? void 0 : _useCurrentProject.value;
  const {
    t
  } = useTranslation();
  const {
    behaviors,
    setBehaviors,
    handleAddBehavior,
    handleDeleteBehavior,
    handleDuplicateBehavior,
    handleUpdateBehavior
  } = useBehaviorsList(props.entry, props.updateEntry);
  const [conversionWindowErrorMessage, setConversionWindowErrorMessage] = React.useState<string | undefined>(undefined);
  const [holdingPropertyConstant, setHoldingPropertyConstant] = React.useState<Segmentation[]>([]);
  const [stageExclusionSteps, setStageExclusionSteps] = React.useState(false);
  const [stageHoldProps, setStageHoldProps] = React.useState(false);
  const hasExcessEvents = behaviors.length > 2;
  const exclusionSteps = (props.entry.behavior.exclusions || []).map(exclusion => mapBookmarkExclusionStepToEventEntry(behaviors.length, exclusion));
  const dataGroupId = getFunnelOrRetentionDataGroupId({
    props
  });
  const funnelReentryMode = props.entry.behavior.funnelReentryMode;
  const behaviorsCount = behaviors.length;
  const dataGroupsEnabled = currentProject === null || currentProject === void 0 ? void 0 : currentProject.canUseCohorts();
  const projectHasPeople = Boolean(currentProject === null || currentProject === void 0 ? void 0 : currentProject.hasIntegratedEngage);
  const projectHasCohorts = Boolean(currentProject === null || currentProject === void 0 ? void 0 : currentProject.canUseCohorts());
  const projectId = props.sectionProps.projectId;
  const workspaceId = props.sectionProps.workspaceId;
  const trackName = props.sectionProps.trackName;
  const projectAsOfDate = currentProject === null || currentProject === void 0 ? void 0 : currentProject.projectAsOfDate;
  const entryRowLabelType = RowLabelType.None;
  const holdingPropertyConstantLimitReached = holdingPropertyConstant.length >= MAX_HOLDING_PROPERTY_CONSTANT;
  const disableAnyOrderMessage = DateRange.hasSubDayGranularity((_props$sectionProps = props.sectionProps) === null || _props$sectionProps === void 0 ? void 0 : _props$sectionProps.dateRange) ? t("reports.chart-types.disabled-reasons.sub-day-granularity-with-any-order") : "";

  // Reset holding property constant when prop-provided value changes
  // TODO: Cleaner/simpler way to do this?
  React.useEffect(() => {
    const incomingHoldingPropertyConstant = props.entry.behavior.aggregateBy || [];
    if (incomingHoldingPropertyConstant.length !== holdingPropertyConstant.length || incomingHoldingPropertyConstant.some((property, index) => property.propertyName !== holdingPropertyConstant[index].propertyName)) {
      setHoldingPropertyConstant(incomingHoldingPropertyConstant);
    }
  }, [props.entry.behavior.aggregateBy]);
  const handleFunnelReentryModeChange = (updatedFunnelReentryMode: FunnelReentryModeType) => {
    const updateEntry = produce(props.entry, entry => {
      entry.behavior.funnelReentryMode = updatedFunnelReentryMode;
      return entry;
    });
    props.updateEntry(updateEntry);
    props.dispatchTrack(QueryEntrySectionTrackingEvent.FunnelReentryModeToggled, {
      funnelReentryMode: updatedFunnelReentryMode.toString()
    });
  };
  const handleConversionCriteriaAdd = (ev: ChangeEvent) => {
    const {
      value
    } = ev.detail.item;
    if (value === ConversionCriteriaAction.ExcludeEvent) {
      setStageExclusionSteps(true);
    } else if (value === ConversionCriteriaAction.HoldProperty) {
      setStageHoldProps(true);
    }
  };

  /**
   * Transforms Segmentation[] entries into properties
   * @todo [pablo] update this in favor of a property selector type
   */
  const handlePropertyConstantUpdate = (update: EntrySectionUpdates<QuerySegmentEntry>) => {
    const properties = update.entries.map(entry => entry.event);
    const updatedEntry = produce(props.entry, entry => {
      entry.behavior.aggregateBy = properties as Segmentation[];
      return entry;
    });
    props.updateEntry(updatedEntry);
    setStageHoldProps(false);
  };
  const handleExclusionEventsUpdate = (_ref: EntrySectionUpdates<QueryEventEntry>) => {
    let {
      entries
    }: EntrySectionUpdates<QueryEventEntry> = _ref;
    const updatedEntry = produce(props.entry, entry => {
      entry.behavior.exclusions = sanitizeExclusionSteps(entries, behaviorsCount).map(step => {
        const subBehaviors = entry.behavior.behaviors || [];
        return mapEventEntryToBookmarkExclusionStep(subBehaviors.length, step);
      });
      return entry;
    });
    props.updateEntry(updatedEntry);
    setStageExclusionSteps(false);
  };

  /**
   * Handles the addition of a new event comparison to the behavior at the specified index.
   *
   * If there is a normal event for the behavior at the current index it gets
   * converted to a simple behavior with a nested behaviors list and then we
   * append the existing event behavior to that list. If there's already a nested
   * event comparison behaviors list, we simply append a new empty behavior to it.
   *
   * @param {number} index - The index of the behavior to update.
   * @returns {void}
   */
  const handleAddEventComparison = (index: number): void => {
    const updatedEntry = produce(props.entry, entry => {
      if (!entry.behavior.behaviors) {
        return entry;
      }
      let behaviorToUpdate: WritableDraft<MetricEntryBehavior> = entry.behavior.behaviors[index];
      if (!behaviorToUpdate) {
        return entry;
      }
      // If there is no existing event comparison, we initialize a nested
      // behaviors array with the current behavior.
      if (!behaviorToUpdate.behaviors) {
        const originalBehavior = behaviorToUpdate;
        behaviorToUpdate = _objectSpread(_objectSpread({}, getEmptyEventMetricEntryBehavior()), {}, {
          type: MetricType.Event
        });
        behaviorToUpdate.behaviors = [originalBehavior];
      }
      // We append a new empty behavior to it.
      behaviorToUpdate.behaviors.push(getEmptyEventMetricEntryBehavior());
      entry.behavior.behaviors[index] = behaviorToUpdate;
      return entry;
    });
    props.updateEntry(updatedEntry);
  };
  return <div data-sentry-component="FunnelBehaviorDefinition" data-sentry-source-file="behavior-definition.tsx">
      <DraggableBehaviorsList {...props} setBehaviors={setBehaviors} data-sentry-element="DraggableBehaviorsList" data-sentry-source-file="behavior-definition.tsx">
        {behaviors.map((subBehavior, index) => {
        if (subBehavior.behaviors) {
          return <EventComparisonRow {...props} behaviors={subBehavior.behaviors} disableAnyOrderMessage={disableAnyOrderMessage} index={index} onUpdate={entry => handleUpdateBehavior(index, entry.behavior)} />;
        }
        const {
          rowIcon,
          toggleAnyOrder: toggleStepAnyOrder
        } = useFunnelAnyOrderIconToggle(props.entry, index, props.updateEntry);
        return <DraggableBehavior {...props} index={index} additionalOverflowControls={[_objectSpread(_objectSpread({}, (ControlBarActionItems as any)[ControlBarAction.AddGroupedEvent]), {}, {
          onSelect: () => handleAddEventComparison(index)
        }), _objectSpread(_objectSpread({}, (ControlBarActionItems as any)[ControlBarAction.ToggleAnyOrder]), {}, {
          onSelect: () => toggleStepAnyOrder()
        }, disableAnyOrderMessage ? {
          isDisabled: true,
          tooltip: {
            text: disableAnyOrderMessage
          }
        } : {})]} behavior={subBehavior}
        /** prevents behavioral or computed filters from showing up */ otherFilterOptions={[]}
        // @todo(metrics) Figure out a way to uniquely identify each behavior in a metric
        //key={index}
        iconLabel={rowIcon ? null : String(index + 1)} label={t("query-builder.select-step")} onDelete={hasExcessEvents ? () => handleDeleteBehavior(index) : null} onDuplicate={() => handleDuplicateBehavior(index)} onSelect={behavior => handleUpdateBehavior(index, behavior)} rowIcon={rowIcon} suffixLabel={index !== behaviors.length - 1 ? " ".concat(t("filters.group-operator.then")) : null} />;
      })}
      </DraggableBehaviorsList>

      {/* @todo(metrics) Update style to match design.*/}
      {!isPlaceholder(props) && <BehaviorEventRow {...props} behavior={_objectSpread(_objectSpread({}, getEmptyEventMetricEntryBehavior()), {}, {
      name: t("query-builder.metrics.add-step")
    })} hideControls isSecondaryRow onSelect={behavior => handleAddBehavior(behavior)} rowIcon='behavior-event-add-step' />}

      <div className={styles.actions}>
        <ConversionCriteria entry={props.entry} onError={err => setConversionWindowErrorMessage(err)} updateEntry={props.updateEntry} data-sentry-element="ConversionCriteria" data-sentry-source-file="behavior-definition.tsx" />

        {/* Funnel Advanced menu */}
        <SelectReact className={styles.advancedMenu} slot={"actions"} theme={SelectTheme.Tertiary} alwaysShowCaret selectedOption={t("query-builder.advanced")} menuAlignment={Directions.Right} menuWidth={245} size={ButtonSize.Small} tooltip={{
        placement: Placement.Right
      }} experimentalPositioning onChange={ev => handleConversionCriteriaAdd(ev as ChangeEvent)} sections={[{
        items: [/* Funnel-Reentry mode */
        {
          isDisabled: false,
          label: t("query-builder.conversion.optimized-reentry"),
          icon: "merge",
          tooltip: OPTIMIZED_REENTRY_TOOLTIP,
          actionsRenderer: () => SwitchEl({
            attrs: {
              on: funnelReentryMode === FunnelReentryModeType.Optimized
            },
            on: {
              change: () => handleFunnelReentryModeChange(funnelReentryMode !== FunnelReentryModeType.Optimized ? FunnelReentryModeType.Optimized : FunnelReentryModeType.Basic)
            }
          })
        }, /** funnel any order setting */
        {
          isDisabled: disableAnyOrderMessage !== "",
          label: _ref2 => {
            let {
              t
            } = _ref2;
            return t("query-builder.conversion.any-order");
          },
          icon: "top-paths",
          tooltip: disableAnyOrderMessage ? {
            text: disableAnyOrderMessage
          } : (_ORDER_OPTIONS$find = ORDER_OPTIONS.find(order => props.entry.behavior.funnelOrder === order.value)) === null || _ORDER_OPTIONS$find === void 0 ? void 0 : _ORDER_OPTIONS$find.tooltip,
          actionsRenderer: () => SwitchEl({
            attrs: {
              on: !disableAnyOrderMessage && props.entry.behavior.funnelOrder === FunnelOrder.Any
            },
            on: {
              change: () => toggleAnyOrder({
                entry: props.entry,
                onUpdate: props.updateEntry
              })
            }
          })
        }, /** funnel exclusion steps */
        {
          icon: "eye-hide",
          label: _ref3 => {
            let {
              t
            } = _ref3;
            return t("query-builder.conversion.exclude-who-did");
          },
          value: ConversionCriteriaAction.ExcludeEvent,
          tooltip: EXCLUSION_EVENTS_TOOLTIP
        }, /** funnel property constant */
        {
          icon: "lock",
          label: _ref4 => {
            let {
              t
            } = _ref4;
            return t("query-builder.conversion.hpc");
          },
          value: ConversionCriteriaAction.HoldProperty,
          tooltip: holdingPropertyConstantLimitReached ? HOLD_PROPERTY_CONSTANT_DISABLED_TOOLTIP : HOLD_PROPERTY_CONSTANT_TOOLTIP,
          isDisabled: holdingPropertyConstantLimitReached
        }]
      }]} data-sentry-element="SelectReact" data-sentry-source-file="behavior-definition.tsx" />
      </div>

      {/* Error message for conversion window input */}
      {conversionWindowErrorMessage && <div className={styles.errorMessage}>{conversionWindowErrorMessage}</div>}

      {/* Exclusion Steps and Holding Property Constant query entries sections */}
      <div>
        {/* exclusion steps */}
        <QueryEntrySectionReact className={styles.queryEntrySection} dataGroupId={dataGroupId} dataGroupsEnabled={dataGroupsEnabled} projectHasPeople={projectHasPeople} projectHasCohorts={projectHasCohorts} projectId={projectId} workspaceId={workspaceId} trackName={trackName} projectAsOfDate={projectAsOfDate} entries={(_exclusionSteps$map = exclusionSteps === null || exclusionSteps === void 0 ? void 0 : exclusionSteps.map(step => _objectSpread(_objectSpread({}, step), {}, {
        prefixLabel: "Exclude "
      }))) !== null && _exclusionSteps$map !== void 0 ? _exclusionSteps$map : []} rowDraggable rowLabelType={RowLabelType.None} entryType={EntryType.ExclusionEvent} includeCustomProperties={IncludeCustomPropertyType.All} showBorrowedProperties disableAddEntry numExclusionSteps={behaviorsCount} rowLabelTypeForExclusionSteps={entryRowLabelType} trackProperties={{
        sectionName: "exclusion-steps"
      }} stagingRow={stageExclusionSteps} onAbortStage={() => setStageExclusionSteps(false)} onUpdate={updates => handleExclusionEventsUpdate(updates as EntrySectionUpdates<QueryEventEntry>)} data-sentry-element="QueryEntrySectionReact" data-sentry-source-file="behavior-definition.tsx" />

        {/* @todo [pablo] we are using a segmentation entry section here as a temporary
         * solution  as we move towards merging filters + segments and creating a new property selector type *
         */}
        <QueryEntrySectionReact className={styles.queryEntrySection} projectId={projectId} workspaceId={workspaceId} trackName={trackName} rowLimit={MAX_HOLDING_PROPERTY_CONSTANT} rowDraggable rowLabelType={RowLabelType.None} includeCustomProperties={IncludeCustomPropertyType.All} showBorrowedProperties disableAddEntry disableMapToLookupTable eventNames={uniq(behaviors.map(behavior => behavior.name!))} propertyCombineMode={PropertyCombineMode.Intersection} rowTypes={[SelectorTypeDefinitions.Segment]} trackProperties={{
        'for hold property constant': true,
        sectionName: "hold-prop-constant"
      }} entries={holdingPropertyConstant.map(prop => ({
        type: SelectorTypeDefinitions.Segment,
        event: prop,
        filters: [],
        prefixLabel: "Hold constant "
      })) as QuerySegmentEntry[]} stagingRow={stageHoldProps} onAbortStage={() => setStageHoldProps(false)} onUpdate={update => handlePropertyConstantUpdate(update as EntrySectionUpdates<QuerySegmentEntry>)} data-sentry-element="QueryEntrySectionReact" data-sentry-source-file="behavior-definition.tsx" />
      </div>
    </div>;
}